/**
 * TeamsTab — second tab on EmployeesPage that lists teams grouped by
 * department. Per TEAMS-SPEC.md.
 *
 * Visual language: warm-ivory via .aura-root primitives. Serif (Fraunces)
 * for page + section titles, sans (Outfit) everywhere else.
 *
 * Gracefully degrades if the backend doesn't yet return
 * `user.headOfDepartmentIds`: non-admins simply see no create affordance
 * and get an "ask admin" empty state instead of a broken CTA.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Plus,
  Search,
  Users,
  Loader2,
  ChevronDown,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';
import { api } from '@/utils/api';
import { listTeams, type Team, type TeamMembership } from '@/api/teams';
import { useTeamPermissions } from '@/hooks/useTeamPermissions';
import { TeamCreateModal } from './TeamCreateModal';
import { TeamDetailDrawer } from './TeamDetailDrawer';
import { countTeamMembers } from './teamMemberCounts';

interface Department {
  id: string;
  name: string;
  code?: string | null;
  headId?: string | null;
}

const COLLAPSED_KEY = 'hr.teamsTab.collapsedDepts';

export function TeamsTab() {
  const perms = useTeamPermissions();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filterDept, setFilterDept] = useState('');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(COLLAPSED_KEY);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [createLockedDeptId, setCreateLockedDeptId] = useState<string | undefined>(undefined);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [detailTeamId, setDetailTeamId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [deptRes, list] = await Promise.all([
        api.get('/departments'),
        listTeams({
          departmentId: filterDept || undefined,
          isActive: showInactive ? undefined : true,
          q: search || undefined,
        }),
      ]);
      setDepartments(deptRes.data?.data ?? deptRes.data ?? []);
      setTeams(list);
    } catch (err: any) {
      // Backend may not yet be deployed — surface but don't crash.
      setError(err?.response?.data?.error || err?.message || 'Failed to load teams.');
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, [filterDept, search, showInactive]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleCollapse = (deptId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(deptId)) next.delete(deptId);
      else next.add(deptId);
      try {
        localStorage.setItem(COLLAPSED_KEY, JSON.stringify(Array.from(next)));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  // Group teams by department. If filterDept is set, only show that one.
  const groups = useMemo(() => {
    const byDept = new Map<string, Team[]>();
    for (const t of teams) {
      const list = byDept.get(t.departmentId) ?? [];
      list.push(t);
      byDept.set(t.departmentId, list);
    }
    const visibleDepts = filterDept
      ? departments.filter((d) => d.id === filterDept)
      : departments;
    return visibleDepts.map((d) => ({
      department: d,
      teams: byDept.get(d.id) ?? [],
    }));
  }, [teams, departments, filterDept]);

  const totalCount = teams.length;

  const openCreate = (deptId?: string) => {
    setEditingTeam(null);
    setCreateLockedDeptId(deptId);
    setCreateOpen(true);
  };

  const openEdit = (team: Team) => {
    setEditingTeam(team);
    setCreateLockedDeptId(team.departmentId);
    setCreateOpen(true);
  };

  const handleSaved = () => {
    load();
  };

  // Determine disabled-state of the top-level create button.
  const topLevelCreateEnabled = perms.canCreateAny || perms.canCreateInDept;

  // Graceful-degrade empty state: user is not admin AND has no headOf entries
  // AND there are no visible teams → show "contact admin" message.
  const showContactAdmin =
    !loading
    && !error
    && totalCount === 0
    && !perms.isAdmin
    && perms.myDeptHeadOf.length === 0;

  return (
    <div className="aura-root">
      {/* Page heading */}
      <div
        className="row between center"
        style={{ marginTop: 4, marginBottom: 4, flexWrap: 'wrap', gap: 12 }}
      >
        <div>
          <h1
            style={{
              fontFamily: 'var(--ff-serif)',
              fontSize: 28,
              color: 'var(--ink-0)',
              margin: 0,
            }}
          >
            Teams
          </h1>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>
            Organise employees into working teams across departments.
          </div>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => openCreate()}
          disabled={!topLevelCreateEnabled}
          title={
            topLevelCreateEnabled
              ? undefined
              : 'Only Department Heads and Admins can create teams.'
          }
          style={{ opacity: topLevelCreateEnabled ? 1 : 0.5 }}
        >
          <Plus size={14} /> New Team
        </button>
      </div>

      {/* Filter row */}
      <div
        className="row center gap-3"
        style={{ margin: '16px 0', flexWrap: 'wrap' }}
      >
        <select
          value={filterDept}
          onChange={(e) => setFilterDept(e.target.value)}
          style={{
            background: 'var(--ivory-0)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-sm)',
            padding: '6px 10px',
            fontSize: 13,
            color: 'var(--ink-0)',
            minWidth: 200,
          }}
        >
          <option value="">All departments</option>
          {departments.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
        <div
          className="relative grow"
          style={{
            background: 'var(--ivory-0)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-sm)',
            minWidth: 240,
            maxWidth: 360,
          }}
        >
          <Search
            size={14}
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--ink-3)',
            }}
          />
          <input
            type="text"
            placeholder="Search teams…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              background: 'transparent',
              border: 'none',
              outline: 'none',
              padding: '6px 10px 6px 30px',
              fontSize: 13,
              width: '100%',
              color: 'var(--ink-0)',
            }}
          />
        </div>
        <label
          className="row center gap-2"
          style={{ fontSize: 12, color: 'var(--ink-2)', cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            style={{ accentColor: 'var(--saffron)' }}
          />
          Show inactive
        </label>
        <div
          style={{
            fontSize: 11,
            color: 'var(--ink-3)',
            marginLeft: 'auto',
          }}
        >
          {totalCount} team{totalCount === 1 ? '' : 's'}
        </div>
      </div>

      {/* Error strip */}
      {error && (
        <div
          className="row center gap-2"
          style={{
            padding: 12,
            borderRadius: 'var(--r-sm)',
            background: 'var(--warn-bg)',
            color: 'var(--warn)',
            fontSize: 12,
            marginBottom: 16,
          }}
        >
          <AlertCircle size={14} />
          <span className="grow">{error}</span>
          <button className="btn btn-ghost btn-sm" onClick={load}>
            Retry
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div
          className="row center"
          style={{
            justifyContent: 'center',
            padding: 60,
            color: 'var(--ink-3)',
            gap: 8,
          }}
        >
          <Loader2 size={18} className="animate-spin" />
          Loading teams…
        </div>
      ) : showContactAdmin ? (
        <div
          className="card row center"
          style={{
            padding: 40,
            flexDirection: 'column',
            gap: 12,
            color: 'var(--ink-2)',
            textAlign: 'center',
          }}
        >
          <Users size={36} color="var(--ink-3)" />
          <div
            style={{
              fontFamily: 'var(--ff-serif)',
              fontSize: 18,
              color: 'var(--ink-0)',
            }}
          >
            No teams visible
          </div>
          <div style={{ fontSize: 13, maxWidth: 400 }}>
            Ask your admin to add you to a team, or — if you should be able to
            create teams — to set you as a Department Head in{' '}
            <strong>Departments</strong>.
          </div>
        </div>
      ) : groups.length === 0 ? (
        <div
          className="card"
          style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}
        >
          No departments configured yet.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 20 }}>
          {groups.map(({ department, teams: deptTeams }) => {
            const isCollapsed = collapsed.has(department.id);
            const canCreateHere =
              perms.isAdmin || perms.myDeptHeadOf.includes(department.id);

            return (
              <section key={department.id}>
                <div
                  className="row center between"
                  style={{
                    marginBottom: 10,
                    paddingBottom: 8,
                    borderBottom: '1px solid var(--line)',
                    cursor: 'pointer',
                  }}
                  onClick={() => toggleCollapse(department.id)}
                >
                  <div className="row center gap-2">
                    {isCollapsed ? (
                      <ChevronRight size={14} color="var(--ink-3)" />
                    ) : (
                      <ChevronDown size={14} color="var(--ink-3)" />
                    )}
                    <h2
                      style={{
                        fontFamily: 'var(--ff-serif)',
                        fontSize: 18,
                        color: 'var(--ink-0)',
                        margin: 0,
                      }}
                    >
                      {department.name}
                    </h2>
                    <span
                      style={{ fontSize: 12, color: 'var(--ink-3)' }}
                    >
                      · {deptTeams.length} team
                      {deptTeams.length === 1 ? '' : 's'}
                    </span>
                  </div>
                </div>

                {!isCollapsed && (
                  <>
                    {deptTeams.length === 0 ? (
                      canCreateHere ? (
                        <div
                          className="card row center"
                          style={{
                            padding: 16,
                            background: 'var(--ivory-2)',
                            border: '1px dashed var(--line)',
                            gap: 12,
                          }}
                        >
                          <Users size={18} color="var(--ink-3)" />
                          <div
                            className="grow"
                            style={{ fontSize: 13, color: 'var(--ink-2)' }}
                          >
                            No teams yet in {department.name}.
                          </div>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openCreate(department.id);
                            }}
                          >
                            <Plus size={12} /> Create one
                          </button>
                        </div>
                      ) : null
                    ) : (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns:
                            'repeat(auto-fill, minmax(240px, 1fr))',
                          gap: 12,
                        }}
                      >
                        {deptTeams.map((t) => (
                          <TeamCard
                            key={t.id}
                            team={t}
                            onOpen={() => setDetailTeamId(t.id)}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </section>
            );
          })}
        </div>
      )}

      {/* Modals + drawer */}
      <TeamCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={handleSaved}
        editing={editingTeam}
        lockedDepartmentId={createLockedDeptId}
      />
      <TeamDetailDrawer
        open={!!detailTeamId}
        teamId={detailTeamId}
        onClose={() => setDetailTeamId(null)}
        onEdit={(team) => {
          setDetailTeamId(null);
          openEdit(team);
        }}
        onChange={load}
      />
    </div>
  );
}

// ── Team card ──────────────────────────────────────────────────────────────
interface TeamCardProps {
  team: Team;
  onOpen: () => void;
}

function TeamCard({ team, onOpen }: TeamCardProps) {
  const [hover, setHover] = useState(false);

  const memberships = team.memberships ?? [];
  const active = memberships.filter((m: TeamMembership) => !m.leftAt);
  const head =
    team.departmentHead
    ?? active.find((m) => m.level === 'DEPARTMENT_HEAD')
    ?? null;
  // bd/AI-5few (S2.1): count the new TEAM_HEAD/FIELD_EXECUTIVE levels (and
  // legacy MANAGER/EXECUTIVE) via the shared pure tally — the old inline
  // filter only matched legacy levels, so real area teams showed 0/0.
  const counts = team.counts ?? countTeamMembers(memberships);

  const headName = head?.user
    ? `${head.user.firstName ?? ''} ${head.user.lastName ?? ''}`.trim()
      || head.user.email
    : null;

  return (
    <div
      className="card"
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: 14,
        display: 'grid',
        gap: 10,
        cursor: 'pointer',
        transition: 'transform 0.12s ease, box-shadow 0.12s ease',
        transform: hover ? 'translateY(-2px)' : 'none',
        boxShadow: hover ? 'var(--sh-3)' : 'var(--sh-1)',
        opacity: team.isActive ? 1 : 0.65,
      }}
    >
      {/* Row 1: name + code + status */}
      <div className="row center gap-2" style={{ flexWrap: 'wrap' }}>
        <div
          className="grow"
          style={{
            fontFamily: 'var(--ff-serif)',
            fontSize: 16,
            color: 'var(--ink-0)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
          title={team.name}
        >
          {team.name}
        </div>
        {team.code && (
          <span className="chip chip-amber mono" style={{ fontSize: 10 }}>
            {team.code}
          </span>
        )}
        {!team.isActive && (
          <span className="chip chip-red" style={{ fontSize: 10 }}>
            Inactive
          </span>
        )}
      </div>

      {/* Row 2: head */}
      <div className="row center gap-2" style={{ minHeight: 24 }}>
        {headName ? (
          <>
            <InitialsBadge name={headName} size={22} />
            <div
              style={{
                fontSize: 12,
                color: 'var(--ink-1)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={headName}
            >
              {headName}
            </div>
            <span
              className="chip chip-level-head"
              style={{ fontSize: 9, marginLeft: 'auto' }}
            >
              Head
            </span>
          </>
        ) : (
          <span
            style={{
              fontSize: 11,
              color: 'var(--ink-3)',
              fontStyle: 'italic',
            }}
          >
            No department head set
          </span>
        )}
      </div>

      {/* Row 3: counts */}
      <div className="row center gap-2">
        <span className="chip chip-level-manager" style={{ fontSize: 10 }}>
          {counts.managers} Manager{counts.managers === 1 ? '' : 's'}
        </span>
        <span className="chip chip-level-executive" style={{ fontSize: 10 }}>
          {counts.executives} Executive{counts.executives === 1 ? '' : 's'}
        </span>
      </div>

      {/* Footer */}
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button
          className="btn btn-outline btn-sm"
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        >
          Open
        </button>
      </div>
    </div>
  );
}

function InitialsBadge({ name, size = 22 }: { name: string; size?: number }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'var(--forest-50)',
        color: 'var(--forest-600)',
        border: '1px solid var(--forest-100)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.4,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {initials || '?'}
    </div>
  );
}
